import "./index.scss";
import React, { useEffect, useState } from "react";
import {
  ApplicationResponse,
  applicationStatuses,
  useApplicationFormDispatch,
  getApplicationDetails,
  getApplicationSelector,
  applicationSteps,
} from "@quest-finance/quest-fe-shared/dist/application";
import { LOADING_STATUS } from "@quest-finance/quest-fe-shared/dist/common/constants/loadingStatuses";
import { parse as qsParse } from "query-string";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router";
import { sprintf } from "sprintf-js";
import { showErrorPopUp } from "../../../../../common/components/ShowNotification/showNotification";
import { processError } from "../../../../../common/utils/error";
import { ExtendedAntd, LayoutTypes } from "../../../../../theme";
import { PORTALS } from "../../../../../theme/constants/Layout";
import {
  useWorkflowDispatch,
  workflowActions,
  workflowSelectors,
} from "../../../../../workflow";
import {
  getAssessmentDetail,
  resetAssessmentDetail,
} from "../../../../actions/creators/assessmentDetail";
import ApplicationOverview from "../../../../components/application-overview/ApplicationOverView";
import Emailer from "../../../../components/Email";
import Navigation from "../../../../components/navigation/Navigation";
import Notes from "../../../../components/notes";
import { NAV_ITEMS } from "../../../../constants/navigation";
import {
  creditAssessment,
  legacyPageMapping,
} from "../../../../constants/routes";
import { useAssessmentDetailsDispatch } from "../../../../dispatchers/index";
import {
  assessmentResponseSelector,
  assessmentResponseStatusSelector,
} from "../../../../selectors/assessmentDetail";
import { AssessmentTypes } from "../../../../types/AssessmentType";
import { Slug } from "../../../../types/Navigation";
import ApplicantGuarantors from "../applicant-guarantors/ApplicantGuarantors";
import AuditLogs from "../AudtiLogs";
import Decision from "../Decision";
import NotesContracts from "../NotesContracts";
import QuotePage from "../Quote";
import RefStatements from "../RefStatements";
import Security from "../Security";
import Settlement from "../Settlement";

const { workflowSelector } = workflowSelectors;

const defaultBreadcrumb: LayoutTypes.BreadcrumbItem[] = [
  {
    label: "Home",
    url: "/assessment/assessments/applications",
  },
];

const CreditEngineRoot: React.FunctionComponent = () => {
  const { assessmentId, section } = useParams() as {
    section: Slug;
    assessmentId: string;
  };
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryParams = qsParse(search) as {
    tab: applicationSteps.APPLICATION_STEPS;
  };
  const dispatchAssessment = useAssessmentDetailsDispatch();
  const workflowDispatch = useWorkflowDispatch();
  const dispatchApplication = useApplicationFormDispatch();
  const application: ApplicationResponse = useSelector(getApplicationSelector);
  const assessment = useSelector(assessmentResponseSelector);
  const assessmentResponseStatus = useSelector(
    assessmentResponseStatusSelector
  );
  const [breadcrumb, setBreadcrumb] =
    useState<LayoutTypes.Breadcrumbs>(defaultBreadcrumb);
  const {
    workflowDetails: { status: workflowStatus, error: workflowError },
  } = useSelector(workflowSelector);
  const emailerContainer = document.getElementById(PORTALS.EMAILER);

  useEffect(() => {
    if (queryParams.tab) {
      const activeTab = legacyPageMapping[queryParams.tab];

      if (!activeTab) {
        navigate("/assessment/assessments/applications", { replace: true });
      } else {
        navigate(sprintf(activeTab, assessmentId), { replace: true });
      }
    }
  }, [navigate, assessmentId, queryParams.tab]);

  useEffect(() => {
    if (
      assessment.externalId &&
      assessment.type === AssessmentTypes.APPLICATION
    ) {
      dispatchApplication(getApplicationDetails(assessment.externalId));
    }
  }, [dispatchApplication, assessment]);

  useEffect(() => {
    const isNotApplication =
      assessmentResponseStatus === LOADING_STATUS.SUCCESS &&
      assessment.id &&
      assessment.type !== AssessmentTypes.APPLICATION;
    if (
      assessmentResponseStatus === LOADING_STATUS.FAILED ||
      isNotApplication
    ) {
      if (isNotApplication) {
        showErrorPopUp("Assessment not found.");
      }
      navigate("/assessment/assessments/applications", { replace: true });
    }
  }, [assessmentResponseStatus, assessment.id, assessment.type, navigate]);

  useEffect(() => {
    if (workflowStatus === LOADING_STATUS.FAILED) {
      processError(workflowError, (errorMessage) => {
        showErrorPopUp(errorMessage);
      });
    }
  }, [workflowStatus, workflowError]);

  useEffect(() => {
    return () => {
      dispatchAssessment(resetAssessmentDetail());
      workflowDispatch(workflowActions.resetWorkflowDetails());
    };
  }, [dispatchAssessment, workflowDispatch]);

  useEffect(() => {
    dispatchAssessment(getAssessmentDetail(assessmentId as string));
  }, [dispatchAssessment, assessmentId, section]);

  useEffect(() => {
    if (application.id && section) {
      const { applicant, applicationNumber, publicStatus } = application;
      const companyName = applicant.tradingName
        ? `${applicant.entityName} (t/a ${applicant.tradingName})`
        : applicant.entityName;
      const status =
        applicationStatuses.APPLICATION_PUBLIC_STATUS_LABELS[publicStatus];

      setBreadcrumb([
        ...defaultBreadcrumb,
        {
          label: `[${status}] - ` + applicationNumber + ": " + companyName,
          url: sprintf(creditAssessment, assessmentId, Slug.QUOTE),
        },
        {
          label: NAV_ITEMS[section].label,
          url: "",
        },
      ]);
    }
  }, [section, assessmentId, application]);

  let content = null;

  if (section === Slug.QUOTE) {
    content = <QuotePage />;
  } else if (section === Slug.APPLICANT_GUARANTORS) {
    content = <ApplicantGuarantors />;
  } else if (section === Slug.REF_STATEMENTS) {
    content = <RefStatements />;
  } else if (section === Slug.SECURITY) {
    content = <Security />;
  } else if (section === Slug.NOTES_CONTRACTS) {
    content = <NotesContracts />;
  } else if (section === Slug.DECISION) {
    content = <Decision />;
  } else if (section === Slug.SETTLEMENT) {
    content = <Settlement />;
  } else if (section === Slug.AUDIT_LOG) {
    content = <AuditLogs />;
  }

  return (
    <ExtendedAntd.DefaultLayout className="loan-admin" breadCrumb={breadcrumb}>
      {emailerContainer && createPortal(<Emailer />, emailerContainer)}
      <Navigation />
      <ApplicationOverview />
      {content}
      {![Slug.AUDIT_LOG, Slug.SETTLEMENT].includes(section) && <Notes />}
    </ExtendedAntd.DefaultLayout>
  );
};

export default CreditEngineRoot;
